import React from 'react';

import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {defineMessages, useIntl} from 'react-intl';

import {Types} from '@wheeebo/polaris-api';
import {
  AsvMarker,
  DroneMarker,
  HoverbikeMarker,
  Map,
  GoogleMapProvider,
  useGoogleMap,
  StatusChip,
  isPlaceholderGeometrics,
  useColorMode,
} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.member.vehicle.LocationCard.header.title',
    defaultMessage: 'Location',
  },
  headerSubTitle: {
    id: 'pages.member.vehicle.LocationCard.header.subTitle',
    defaultMessage: 'Latitude, longitude and altitude of the vehicle',
  },
});

/** Tokyo area default when API omits coordinates (JSON omitempty) or no last_geometrics row yet. */
const MAP_CENTER_FALLBACK = {lat: 35.465, lng: 139.76} as const;

interface MapProps {
  apiKey: string;
  vehicle: Types.Vehicle;
  geometrics: Types.Geometrics;
  path?: Types.LatLngAltTime[];
  /** When false, map still loads but vehicle marker is hidden (no misleading position). */
  showVehicleMarker: boolean;
}

function VehicleMap({apiKey, vehicle, geometrics, path, showVehicleMarker}: MapProps): JSX.Element {
  const {setCenter, setBounds} = useGoogleMap();
  const hasFiniteLatLng =
    Number.isFinite(geometrics.latitude) && Number.isFinite(geometrics.longitude);
  const center = hasFiniteLatLng ? {lat: geometrics.latitude, lng: geometrics.longitude} : MAP_CENTER_FALLBACK;
  const props = {
    disablePopup: true,
    vehicleId: vehicle.id,
    position: center,
    heading: geometrics.heading,
  };

  React.useEffect(() => {
    if (path && path.length > 0) {
      setBounds?.(path.map((e) => ({lat: e.lat, lng: e.lng})));
    } else {
      setCenter?.(center);
    }
  }, [path, setCenter, setBounds, center.lat, center.lng]);

  return (
    <Map apiKey={apiKey} hideSearchBar gestureHandling="greedy" paths={path ? [path] : undefined} highlightPath>
      {showVehicleMarker && vehicle.vehicleType === Types.VehicleTypeDrone ? (
        <DroneMarker {...props} />
      ) : showVehicleMarker && vehicle.vehicleType === Types.VehicleTypeAsv ? (
        <AsvMarker {...props} />
      ) : showVehicleMarker && vehicle.vehicleType === Types.VehicleTypeHoverbike ? (
        <HoverbikeMarker {...props} />
      ) : null}
    </Map>
  );
}

interface Props {
  apiKey: string;
  vehicle: Types.Vehicle;
  realtimeMetrics?: Types.Geometrics[];
  lastMetrics?: Types.Geometrics;
  path?: Types.LatLngAltTime[];
}

function pickLatestGeometricsFrame(
  realtime: Types.Geometrics[] | undefined,
  last: Types.Geometrics | undefined,
): Types.Geometrics | undefined {
  const rows =
    realtime?.filter(
      (e) =>
        Number.isFinite(e.latitude) &&
        Number.isFinite(e.longitude) &&
        !isPlaceholderGeometrics(e),
    ) ?? [];
  const bestRt = rows.length > 0 ? rows.reduce((a, b) => (a.time >= b.time ? a : b)) : undefined;
  if (!last) {
    return bestRt;
  }
  if (!bestRt) {
    // DB に last_geometrics 行が無いと GORM はゼロ値を返す。地図・表はゼロ表示でもマウントし、マーカー位置は後で更新される。
    return last;
  }
  return bestRt.time >= last.time ? bestRt : last;
}

function LocationCard({apiKey, vehicle, realtimeMetrics, lastMetrics, path}: Props): JSX.Element {
  const intl = useIntl();
  const {isThemeDark} = useColorMode();
  const lastFrame = pickLatestGeometricsFrame(realtimeMetrics, lastMetrics ?? undefined);
  const hasLatitude = Number.isFinite(lastFrame?.latitude);
  const hasLongitude = Number.isFinite(lastFrame?.longitude);
  const hasAltitude = Number.isFinite(lastFrame?.globalAltitude);
  const showVehicleMarker = hasLatitude && hasLongitude;
  const mapGeometrics: Types.Geometrics | undefined = lastFrame
    ? showVehicleMarker
      ? lastFrame
      : {...lastFrame, latitude: MAP_CENTER_FALLBACK.lat, longitude: MAP_CENTER_FALLBACK.lng}
    : {
        time: 0,
        latitude: MAP_CENTER_FALLBACK.lat,
        longitude: MAP_CENTER_FALLBACK.lng,
        globalAltitude: 0,
        relativeAltitude: 0,
        heading: 0,
      };
  return (
    <GoogleMapProvider>
      <Card>
        <CardHeader
          title={intl.formatMessage(msgs.headerTitle)}
          subheader={intl.formatMessage(msgs.headerSubTitle)}
          action={
            <StatusChip
              realtimeMetrics={realtimeMetrics}
              lastMetrics={lastMetrics}
              hasField={(e: Types.MetricsBase) =>
                Number.isFinite((e as Types.Geometrics).latitude) &&
                Number.isFinite((e as Types.Geometrics).longitude)
              }
            />
          }
        />
        <CardContent>
          <Box sx={{position: 'relative'}}>
            <Paper variant="outlined" sx={{height: 420, position: 'relative', background: 'transparent'}}>
              <VehicleMap
                key={isThemeDark ? 'dark' : 'light'}
                apiKey={apiKey}
                vehicle={vehicle}
                geometrics={mapGeometrics}
                path={path}
                showVehicleMarker={showVehicleMarker}
              />
            </Paper>
            <Paper variant="outlined" sx={{p: 1, width: 'min(360px, calc(100vw - 16px))', position: 'absolute', right: 1, top: 1}}>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Latitude</TableCell>
                      <TableCell align="right">Longitude</TableCell>
                      <TableCell align="right">Altitude</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="right">
                        {hasLatitude ? `${lastFrame?.latitude.toFixed(5)} °` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {hasLongitude ? `${lastFrame?.longitude.toFixed(5)} °` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {hasAltitude ? `${lastFrame?.globalAltitude.toFixed(2)} m` : '-'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </CardContent>
      </Card>
    </GoogleMapProvider>
  );
}

// Don't memoize LocationCard — it needs to re-render when isThemeDark changes
export default LocationCard;
